import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/authContext.jsx';
import { useEstadoApp } from '../context/EstadoAppContext.jsx';
import { useLocalStorage } from '../hooks/useLocalStorage.js';

// Burbuja flotante global del Chat Inteligente (Fase 0).
// Usa el endpoint POST /api/obras/chat (soloAdmin en el backend) y muestra:
//   - el texto sintetizado (plantillas) como burbuja del bot,
//   - las obras top como tarjetas (doble clic → editar la obra en /inicio),
//   - sugerencias proactivas como botones de continuación.
// Solo se muestra para usuario con rol 'admin' (Fase 0; en Fase 2 se extiende).
const ChatBurbujaGlobal = () => {
    const { token, role } = useContext(AuthContext) || {};

    // Solo admin por ahora (política del endpoint: POST /api/obras/chat es
    // soloAdmin, así que a un usuario normal le respondería 403).
    if (role !== 'admin') return null;

    return <ChatGlobalInner token={token} />;
};

const saludo = 'Hola 👋 Soy tu asistente de obras. Preguntame en lenguaje natural, por ejemplo: "obras en Moreno", "obras sin pagos", o "obras con avance alto".';

const ChatGlobalInner = ({ token }) => {
    const [abierto, setAbierto] = useState(false);
    const [input, setInput] = useState('');
    const [mensajes, setMensajes] = useState([]); // { rol: 'bot'|'user'|'cargando', texto, obras?, sugerencias? }
    const [cargando, setCargando] = useState(false);
    // Memoria de contexto: subconjunto de obras del último resultado + historial.
    const [contexto, setContexto] = useState({ ids: [], historial: [] });
    const finRef = useRef(null);

    const navigate = useNavigate();
    const { cambiarEstadoApp } = useEstadoApp();
    const { setItem } = useLocalStorage();

    // Doble clic sobre una obra → editar en /inicio (patrón de TablaObras).
    const editarObra = (idPartidoObra) => {
        if (!idPartidoObra) return;
        setItem('idPartidoObra', idPartidoObra);
        setItem('isEditingPagos', 'false');
        cambiarEstadoApp('edit_desde_pagos', {
            obra: idPartidoObra,
            origen: 'chat',
        });
        navigate(`/inicio?id_partido_obra=${encodeURIComponent(idPartidoObra)}`);
    };

    useEffect(() => {
        if (abierto && mensajes.length === 0) {
            setMensajes([{ rol: 'bot', texto: saludo }]);
            setContexto({ ids: [], historial: [] });
        }
    }, [abierto]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        finRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [mensajes, cargando]);

    const enviar = async (textoRaw) => {
        const texto = (textoRaw ?? input).trim();
        if (!texto || cargando) return;
        if (!token) {
            setMensajes((prev) => [...prev, { rol: 'user', texto }, { rol: 'bot', texto: 'No estás autenticado. Iniciá sesión para usar el chat.' }]);
            setInput('');
            return;
        }

        setMensajes((prev) => [...prev, { rol: 'user', texto }]);
        setCargando(true);
        setInput('');

        try {
            const res = await fetch('/api/obras/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ consulta: texto, contexto }),
            });
            const data = await res.json();
            if (!res.ok) {
                const errMsg = data?.message || 'Ocurrió un error al responder.';
                setMensajes((prev) => [...prev, { rol: 'bot', texto: errMsg }]);
            } else {
                // Actualizar la memoria de contexto con la devuelta por el backend.
                if (data?.contexto) setContexto(data.contexto);
                setMensajes((prev) => [
                    ...prev,
                    {
                        rol: 'bot',
                        texto: data.texto || data.resumen || 'Listo.',
                        obras: data.obras || [],
                        sugerencias: data.sugerencias || [],
                        debug: data.debug || null,
                        chat_log_id: data.chat_log_id || null,
                        engine: data.engine || null,
                        proveedorLLM: data.proveedorLLM || null,
                    },
                ]);
            }
        } catch (err) {
            console.error('[chat] error:', err);
            setMensajes((prev) => [...prev, { rol: 'bot', texto: 'No pude conectarme con el servidor. Probá de nuevo en unos segundos.' }]);
        } finally {
            setCargando(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') enviar();
    };

    // Valora una respuesta del bot (👍/+1 o 👎/-1). No bloquea la UI ante errores.
    // Marca el mensaje como valorado para que quede persistido en el clima local y
    // solo se pueda valorar una vez (una de las dos opciones).
    const valorarRespuesta = async (chatLogId, value) => {
        if (!token || !chatLogId) return;
        // Persistir la valoración en el mensaje (una sola vez).
        setMensajes((prev) =>
            prev.map((m) => (m.chat_log_id === chatLogId ? { ...m, valoracion: value } : m))
        );
        try {
            await fetch('/api/obras/chat/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ chat_log_id: chatLogId, value }),
            });
        } catch (err) {
            console.error('[chat feedback] error:', err);
        }
    };

    return (
        <>
            {/* Botón flotante.
                Está más arriba (bottom-[5.5rem]) porque abajo a la derecha, en
                la misma esquina, vive ahora el botón del chat de soporte
                ("Panel ADMIN" / "Conectar con ADMIN"): forman una columna con
                el Chat Inteligente SIEMPRE arriba, así cuando el Chat
                Inteligente esté disponible para todos (hoy es solo admin) no
                hay que mover nada. */}
            <button
                type="button"
                onClick={() => setAbierto((v) => !v)}
                title="Chat inteligente"
                className="fixed bottom-[5.5rem] right-5 z-50 w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 flex items-center justify-center text-2xl"
            >
                {abierto ? '✕' : '💬'}
            </button>

            {/* Panel: sube con la burbuja, para que el ✕ de la burbuja (que
                queda abajo) siga siendo clickeable. */}
            {abierto && (
                <div className="fixed bottom-[9.5rem] right-5 z-50 w-[380px] max-w-[calc(100vw-2.5rem)] h-[520px] max-h-[calc(100vh-11rem)] bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
                    {/* Header */}
                    <div className="px-4 py-3 bg-blue-600 text-white">
                        <div className="flex items-center justify-between">
                            <p className="font-semibold text-sm">Chat Inteligente</p>
                            <button type="button" onClick={() => setAbierto(false)} className="text-white/80 hover:text-white text-lg leading-none">✕</button>
                        </div>
                        <p className="text-xs text-white/70 mt-0.5">Asistente de obras · búsqueda en lenguaje natural</p>
                    </div>

                    {/* Mensajes */}
                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 dark:bg-gray-900">
                        {mensajes.map((m, i) => <Burbuja key={i} m={m} onSugerencia={enviar} onEditarObra={editarObra} onFeedback={valorarRespuesta} />)}
                        {cargando && (
                            <div className="flex justify-start">
                                <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-bl-sm">
                                    <span className="inline-block animate-pulse">Escribiendo…</span>
                                </div>
                            </div>
                        )}
                        <div ref={finRef} />
                    </div>

                    {/* Input */}
                    <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            disabled={cargando}
                            placeholder="Escribí tu consulta…"
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            type="button"
                            onClick={() => enviar()}
                            disabled={cargando || !input.trim()}
                            className="px-4 py-2 rounded-md text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Enviar
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

// Burbuja individual del chat.
const Burbuja = ({ m, onSugerencia, onEditarObra, onFeedback }) => {
    const esBot = m.rol === 'bot';
    const [verDebug, setVerDebug] = useState(false);
    // La valoración la maneja el padre y se persiste en el mensaje (m.valoracion),
    // así sobrevive a re-renders/cierres y solo se puede valorar una vez.
    const valoracion = m.valoracion || null;

    // Etiqueta de cómo se generó la respuesta: plantilla o modelo (con proveedor).
    const tipoRespuesta = (() => {
        if (!esBot) return null;
        if (m.proveedorLLM) return { kind: 'modelo', label: `Modelo (${m.proveedorLLM})` };
        if (m.engine === 'gemini-rag') return { kind: 'modelo', label: 'Modelo' };
        return { kind: 'plantilla', label: 'Plantilla' };
    })();

    const marcarValoracion = (v) => {
        if (valoracion === v) return; // misma opción: no hace nada.
        onFeedback && onFeedback(m.chat_log_id, v); // el padre persiste + backend UPSERT.
    };
    return (
        <div className={`flex ${esBot ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${esBot ? 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-bl-sm' : 'bg-blue-500 text-white rounded-br-sm'}`}>
                <p className="whitespace-pre-wrap">{m.texto}</p>

                {/* Obras del resultado — doble clic para editar */}
                {m.obras && m.obras.length > 0 && (
                    <div className="mt-2 space-y-2">
                        {m.obras.map((o, j) => (
                            <div
                                key={j}
                                onDoubleClick={() => onEditarObra(o.id_partido_obra)}
                                title={o.id_partido_obra ? 'Doble clic para editar' : undefined}
                                className="rounded-lg border border-gray-200 dark:border-gray-600 p-2 bg-gray-50 dark:bg-gray-900 cursor-pointer select-none hover:border-blue-400"
                            >
                                {o._tipoTarjeta === 'proyecto' || o._tipoTarjeta === 'estado' ? (
                                    <>
                                        <p className="font-medium text-gray-900 dark:text-white">{o.nombre_obra}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {o.count > 1 ? `${o.count} obras` : '1 obra'}
                                            {o.ids?.length ? ` · ${o.ids.join(', ')}` : ''}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="font-medium text-gray-900 dark:text-white">{o.nombre_obra}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {[o.partido, o.estado, o.contratista ? `Contratista: ${o.contratista}` : null]
                                                .filter(Boolean)
                                                .join(' · ') || o.id_partido_obra}
                                        </p>
                                        {(o.porc_avance != null || o.num_pagos > 0) && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                {o.porc_avance != null ? `Avance: ${o.porc_avance}% · ` : ''}
                                                {`Pagos: ${o.num_pagos}`}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Sugerencias proactivas */}
                {m.sugerencias && m.sugerencias.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {m.sugerencias.map((s, j) => (
                            <button
                                key={j}
                                type="button"
                                onClick={() => onSugerencia(s)}
                                className="px-3 py-1 text-xs rounded-full border border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/40 bg-white dark:bg-gray-800"
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                )}

                {/* Debug: prompt enviado + contexto de obras */}
                {esBot && m.debug && (
                    <div className="mt-2 border border-dashed border-gray-400 dark:border-gray-500 rounded-lg p-2">
                        <button
                            type="button"
                            onClick={() => setVerDebug(v => !v)}
                            className="flex items-center gap-1 text-[10px] font-mono text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                            <svg className={`w-3 h-3 transition-transform ${verDebug ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            Debug · {m.debug.tipo || '—'} · {m.debug.nObrasContexto ?? 0} obra(s) en contexto
                        </button>
                        {verDebug && (
                            <div className="mt-2 space-y-2">
                                {m.debug.ids && m.debug.ids.length > 0 && (
                                    <div>
                                        <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-0.5">IDs del contexto:</p>
                                        <p className="text-[10px] font-mono text-gray-600 dark:text-gray-300 break-words">
                                            {m.debug.ids.join(', ')}
                                        </p>
                                    </div>
                                )}
                                {m.debug.llmPrompt && (
                                    <div>
                                        <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Prompt LLM:</p>
                                        <pre className="text-[10px] font-mono text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words max-h-40 overflow-auto">
                                            {m.debug.llmPrompt}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Indicador de origen de la respuesta + valoración */}
                {esBot && (
                    <div className="mt-2 flex items-center gap-2">
                        <span
                            className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                                tipoRespuesta?.kind === 'modelo'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                        >
                            {tipoRespuesta?.label || '—'}
                        </span>
                        {m.chat_log_id && (
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    title="Me sirvió"
                                    onClick={() => marcarValoracion(1)}
                                    className={`px-2 py-0.5 text-sm rounded hover:bg-green-100 dark:hover:bg-green-900/40 ${
                                        valoracion === 1
                                            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                                            : 'text-gray-400'
                                    }`}
                                >
                                    👍
                                </button>
                                <button
                                    type="button"
                                    title="No me sirvió"
                                    onClick={() => marcarValoracion(-1)}
                                    className={`px-2 py-0.5 text-sm rounded hover:bg-red-100 dark:hover:bg-red-900/40 ${
                                        valoracion === -1
                                            ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                            : 'text-gray-400'
                                    }`}
                                >
                                    👎
                                </button>
                                {valoracion && (
                                    <span className="text-[10px] text-gray-400 ml-1">cambiar</span>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatBurbujaGlobal;